/**
 * 矢量 PDF 导出（jsPDF + svg2pdf.js）。
 *
 * 三件事分得清楚：
 *  · 本文件**不 import jspdf** —— 页面盒换算、文件名、中文判定都是纯函数（有单测），
 *    真正调用 jsPDF 的部分走 `PdfDeps` 注入（与 core/io/raster.ts 同一套做法）。
 *  · 为什么注入：svg2pdf.js 官方声明 **不支持 JSDOM**，转换那一步在 jsdom 套件里跑不了，
 *    只能注入假实现；真机保真度（渐变 / 虚线 / 旋转文本）另见 docs/BROWSER-CHECK.md。
 *  · 非 ASCII 字符：jsPDF 内置只有 14 个西文标准字体，**中文不 addFont 就会丢字**。
 *    所以先判定 needsFont，再由 deps.useFont 决定字体从哪来（本文件不管这层）。
 */
import { fileBaseName } from './raster';

/** PDF 的长度单位是 pt（1pt = 1/72 inch）；96dpi 下 1 CSS px = 0.75pt */
export const PT_PER_PX = 0.75;
export const mmToPt = (mm: number): number => (mm / 25.4) * 72;

/**
 * 页面规格：
 *  · `content` = 页面盒就是图幅，无白边（矢量 PDF 最可预期的形态，默认）
 *  · `a4` / `a4l` = A4 竖 / 横，内容等比居中、四周留 10mm
 */
export type PdfPage = 'content' | 'a4' | 'a4l';

const A4_PT = { w: mmToPt(210), h: mmToPt(297) };
const A4_MARGIN_PT = mmToPt(10);

export interface PdfPageBox {
  /** 页面盒（pt） */
  width: number;
  height: number;
  /** 内容在页面里的落点与尺寸（pt） */
  x: number;
  y: number;
  contentWidth: number;
  contentHeight: number;
  /** 内容相对原图幅的缩放（A4 适配会 ≠ 1） */
  scale: number;
  orientation: 'p' | 'l';
}

export interface PdfPageInput {
  /** 图幅（用户单位 = viewBox 尺寸） */
  width: number;
  height: number;
  page?: PdfPage;
}

export function pdfPageBox(input: PdfPageInput): PdfPageBox {
  const w = Math.max(1, Number(input.width) || 0);
  const h = Math.max(1, Number(input.height) || 0);
  const baseW = w * PT_PER_PX;
  const baseH = h * PT_PER_PX;

  if ((input.page ?? 'content') === 'content') {
    return {
      width: baseW,
      height: baseH,
      x: 0,
      y: 0,
      contentWidth: baseW,
      contentHeight: baseH,
      scale: 1,
      orientation: baseW >= baseH ? 'l' : 'p',
    };
  }

  const landscape = input.page === 'a4l';
  const width = landscape ? A4_PT.h : A4_PT.w;
  const height = landscape ? A4_PT.w : A4_PT.h;
  const availW = width - A4_MARGIN_PT * 2;
  const availH = height - A4_MARGIN_PT * 2;
  // 「尽量用满」：小图放大（矢量放大无损）、大图缩小，两个方向取更紧的那个
  const scale = Math.min(availW / baseW, availH / baseH);
  const contentWidth = baseW * scale;
  const contentHeight = baseH * scale;
  return {
    width,
    height,
    x: (width - contentWidth) / 2,
    y: (height - contentHeight) / 2,
    contentWidth,
    contentHeight,
    scale,
    orientation: landscape ? 'l' : 'p',
  };
}

/** `figure.svg` → `figure.pdf`（路径分隔符一并抹平） */
export function pdfFileName(sourceName: string | null | undefined): string {
  return `${fileBaseName(sourceName)}.pdf`;
}

/** 含非 ASCII（中文 / 日文 / emoji……）→ PDF 必须自带字体，否则丢字 */
export function hasNonAscii(text: string): boolean {
  return /[^\x00-\x7F]/.test(text);
}

/* ---------------------------------------------------------------- 编排 */

/** jsPDF 实例的不透明句柄：本文件只在自己与 deps 之间传递，不碰它的内部 */
export type PdfDoc = object;

export interface PdfDeps {
  /** 建文档。真实实现：`new jsPDF({ unit: 'pt', format: [w, h], orientation })` */
  createDoc(box: PdfPageBox): PdfDoc;
  /** 画。真实实现：离屏挂载 → svg2pdf 的 `doc.svg(el, box)` → 卸载 */
  draw(doc: PdfDoc, svgText: string, box: PdfPageBox): Promise<void>;
  /** 出字节。真实实现：`doc.output('blob')` */
  output(doc: PdfDoc): Blob;
  /** 文档含非 ASCII 时注册字体。缺省 = 不注册，中文会丢 —— 由调用方承担 */
  useFont?(doc: PdfDoc): Promise<void>;
}

export interface PdfRequest {
  svgText: string;
  /** 图幅（用户单位）；页面盒由它换算 */
  width: number;
  height: number;
  page?: PdfPage;
  sourceName?: string | null;
}

export interface PdfResult {
  blob: Blob;
  filename: string;
  box: PdfPageBox;
  /** 这份文档是否需要中文字体（true 而 deps 没给 useFont → 中文会丢） */
  needsFont: boolean;
}

export async function renderPdf(req: PdfRequest, deps: PdfDeps): Promise<PdfResult> {
  const box = pdfPageBox({ width: req.width, height: req.height, page: req.page });
  const doc = deps.createDoc(box);
  const needsFont = hasNonAscii(req.svgText);
  // 顺序要紧：字体必须在 draw 之前注册，否则那一次转换已经按西文字体排过版了
  if (needsFont && deps.useFont) await deps.useFont(doc);
  await deps.draw(doc, req.svgText, box);
  return { blob: deps.output(doc), filename: pdfFileName(req.sourceName), box, needsFont };
}
